import {
  isTrackReference,
  useConnectionQualityIndicator,
  useIsSpeaking,
  useTrackMutedIndicator,
  VideoTrack,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { ConnectionQuality, Track } from 'livekit-client';
import { Maximize2, MicOff, Minimize2, MonitorUp, Pin, SignalLow } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import type { FullscreenControl } from '@/hooks/useFullscreen';
import { cn, initials } from '@/lib/utils';

/**
 * One participant's video, or a dignified stand-in when there is none.
 *
 * The `lk-*` class names are kept from the prefab this replaces: the browser
 * checks in scripts/ count tiles and read names through them, and they are a
 * cheaper contract to honour than to rewrite.
 */
export function ParticipantTile({
  trackRef,
  focused = false,
  onToggleFocus,
  canFocus = false,
  bare = false,
  fullscreen,
}: {
  trackRef: TrackReferenceOrPlaceholder;
  focused?: boolean;
  onToggleFocus?: () => void;
  canFocus?: boolean;
  /**
   * Fullscreen for the stage this tile sits on, owned above so that going
   * fullscreen keeps the control bar and the filmstrip on screen. Passed only
   * to the tile that earns the button — the shared screen.
   */
  fullscreen?: FullscreenControl;
  /**
   * Drops the tile's own background and ring, and sizes it to the video's own
   * aspect ratio instead of filling the stage. Used for a screen share on the
   * focus stage.
   *
   * Filling and letterboxing would leave the name plate stranded at the bottom
   * of a tall black box with the picture floating somewhere in the middle of
   * it. Matching the ratio means the tile *is* the picture: no dead space, and
   * the plate sits on the video where it belongs.
   */
  bare?: boolean;
}) {
  const { participant, source } = trackRef;
  const { isMuted } = useTrackMutedIndicator(trackRef);
  const isSpeaking = useIsSpeaking(participant);
  const { quality } = useConnectionQualityIndicator({ participant });

  const isScreenShare = source === Track.Source.ScreenShare;
  const hasVideo = isTrackReference(trackRef) && !isMuted;
  const name = participant.name || participant.identity;
  const weak = quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost;

  // Only the camera is mirrored. A mirrored screen share would render every
  // line of shared code backwards.
  const mirrored = participant.isLocal && !isScreenShare;

  // Shared text is unreadable inside a phone-sized tile, and rotating to
  // landscape only helps if the video can take the whole screen. iOS Safari
  // does not allow fullscreen on arbitrary elements, hence the capability check
  // rather than a button that silently does nothing.
  const canFullscreen = isScreenShare && hasVideo && fullscreen?.supported;

  // Reported by the publisher, so the tile can match the shared window rather
  // than assume 16:9 — people share portrait phones and 4:3 slide decks too.
  const dimensions = trackRef.publication?.dimensions;
  const fitAspect = bare && dimensions && dimensions.width > 0;

  return (
    <div
      style={fitAspect ? { aspectRatio: `${dimensions.width} / ${dimensions.height}` } : undefined}
      className={cn(
        // Sizing is load-bearing. Left at `height: auto` the tile shrinks to
        // the video's intrinsic height, so a focused share renders as a band
        // across the top of the stage with dead space beneath it.
        'lk-participant-tile group relative isolate flex min-h-0 min-w-0 items-center justify-center overflow-hidden transition-[box-shadow,ring-color] duration-200',
        fitAspect ? 'max-h-full max-w-full' : 'size-full',
        bare
          ? 'rounded-lg'
          : cn(
              'rounded-xl bg-[oklch(0.13_0.015_265)] ring-1',
              isSpeaking && !isScreenShare
                ? 'ring-primary/80 shadow-[0_0_0_1px_var(--primary),0_0_28px_-6px_var(--primary)]'
                : 'ring-white/8',
            ),
      )}
      data-lk-speaking={isSpeaking || undefined}
      data-lk-source={source}
    >
      {hasVideo ? (
        <VideoTrack
          trackRef={trackRef}
          className={cn(
            'size-full',
            // Screen shares are letterboxed; faces are cropped to fill. Cropping
            // a shared screen would hide whatever sits at its edges.
            isScreenShare ? 'object-contain' : 'object-cover',
            mirrored && 'scale-x-[-1]',
          )}
        />
      ) : (
        <div className="lk-participant-placeholder absolute inset-0 grid place-items-center">
          <Avatar className="size-[clamp(3rem,18%,6rem)] min-h-12 min-w-12 ring-1 ring-white/10">
            <AvatarFallback className="text-[clamp(0.9rem,4cqw,1.6rem)]">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
        </div>
      )}

      {/* Name plate. Sits on its own scrim so it stays readable over a bright
          window behind the participant. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2.5">
        <span className="flex min-w-0 items-center gap-1.5 rounded-lg bg-black/55 px-2 py-1 text-xs font-medium backdrop-blur-sm">
          {isMuted && !isScreenShare && (
            <MicOff className="size-3.5 shrink-0 text-[color-mix(in_oklab,var(--destructive)_80%,white)]" />
          )}
          {isScreenShare && <MonitorUp className="size-3.5 shrink-0 text-[var(--success)]" />}
          <span className="lk-participant-name truncate">{name}</span>
          {participant.isLocal && <span className="text-white/50">(you)</span>}
          {isScreenShare && <span className="text-white/50">screen</span>}
        </span>

        {weak && (
          <span
            title="Weak connection"
            className="grid size-6 shrink-0 place-items-center rounded-md bg-black/55 backdrop-blur-sm"
          >
            <SignalLow className="size-3.5 text-[var(--warning)]" />
          </span>
        )}
      </div>

      <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
        {canFullscreen && fullscreen && (
          <TileAction
            onClick={fullscreen.toggle}
            label={fullscreen.active ? 'Exit full screen' : 'View full screen'}
            // Always visible: on a phone this is the only way to read the
            // share, so it must not be hidden behind a hover that never comes.
            alwaysVisible
          >
            {fullscreen.active ? (
              <Minimize2 className="size-3.5" />
            ) : (
              <Maximize2 className="size-3.5" />
            )}
          </TileAction>
        )}

        {canFocus && onToggleFocus && (
          <TileAction
            onClick={onToggleFocus}
            label={focused ? `Unpin ${name}` : `Pin ${name}`}
            pressed={focused}
            alwaysVisible={focused}
          >
            <Pin className={cn('size-3.5', focused && 'fill-current')} />
          </TileAction>
        )}
      </div>
    </div>
  );
}

function TileAction({
  onClick,
  label,
  pressed,
  alwaysVisible = false,
  children,
}: {
  onClick: () => void;
  label: string;
  pressed?: boolean;
  alwaysVisible?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      className={cn(
        'focus-visible:outline-ring grid size-8 place-items-center rounded-lg bg-black/55 text-white/80 backdrop-blur-sm transition-opacity hover:text-white focus-visible:outline-2',
        // Hidden until hover on pointer devices, always visible on touch
        // where there is no hover to discover it with.
        alwaysVisible
          ? 'opacity-100'
          : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100',
      )}
    >
      {children}
    </button>
  );
}
